import { useCallback, useMemo, useState } from 'react';

type NormalizeOptions = {
	min?: number;
	max?: number;
	round?: (value: number) => number;
};

type UseBoundedNumberStorageOptions = NormalizeOptions & {
	storageKey: string;
	defaultValue: number;
	parse?: (raw: string) => number;
};

const clampValue = (value: number, { min, max }: NormalizeOptions) => {
	let result = value;
	if (typeof min === 'number') {
		result = Math.max(result, min);
	}
	if (typeof max === 'number') {
		result = Math.min(result, max);
	}
	return result;
};

const normalizeValue = (value: number, options: NormalizeOptions) => {
	const { round } = options;
	const clamped = clampValue(value, options);
	const rounded = round ? round(clamped) : clamped;
	return clampValue(rounded, options);
};

const readStoredNumber = (storageKey: string, defaultValue: number, options: NormalizeOptions, parse: (raw: string) => number) => {
	const fallback = normalizeValue(defaultValue, options);
	if (typeof window === 'undefined') {
		return fallback;
	}

	const raw = localStorage.getItem(storageKey);
	if (raw == null) {
		return fallback;
	}

	const parsed = parse(raw);
	if (!Number.isFinite(parsed)) {
		return fallback;
	}

	return normalizeValue(parsed, options);
};

/**
 * Keeps a number in sync with localStorage with optional bounds and rounding.
 */
export function useBoundedNumberStorage({
	storageKey,
	defaultValue,
	min,
	max,
	round,
	parse = parseFloat,
}: UseBoundedNumberStorageOptions): [number, (value: number) => void] {
	const normalizer = useMemo(() => ({ min, max, round }), [min, max, round]);

	const [value, setValue] = useState<number>(() => readStoredNumber(storageKey, defaultValue, normalizer, parse));

	const setBoundedValue = useCallback(
		(next: number) => {
			const normalized = normalizeValue(next, normalizer);
			setValue(normalized);

			if (typeof window !== 'undefined') {
				localStorage.setItem(storageKey, normalized.toString());
			}
		},
		[normalizer, storageKey],
	);

	return [value, setBoundedValue];
}
